import React, { useState, useEffect } from 'react'
import { MessageCircle, PlayCircle, StopCircle, QrCode, CheckCircle, AlertCircle, Loader, Eye, EyeOff } from 'lucide-react'
import Button from './ui/Button'
import Input from './ui/Input'
import { useToast } from './Toast'
import apiService from '../services/api'

export interface WhatsAppStatus {
  configured: boolean
  ready: boolean
  hasQRCode: boolean
  sessionName?: string
  qrCode?: string
  clientInfo?: {
    name?: string
    phone?: string
    platform?: string
    connected?: boolean
  }
}

export interface GeminiConfig {
  apiKey: string
  model: string
  enabled: boolean
  companySettings?: {
    name: string
    address: string
    phone: string
    email: string
    workingHours: string
    specialties: string
    warrantyPeriod: string
    averageRepairTime: string
    emergencyContact?: string
  }
  aiSettings?: {
    treatmentStyle: 'formal' | 'informal' | 'friendly' | 'professional'
    autoRecognizeCustomers: boolean
    accessToOrderHistory: boolean
    accessToCustomerData: boolean
    accessToInventory: boolean
    canProvideQuotes: boolean
    canScheduleAppointments: boolean
    maxResponseLength: number
    greetingMessage: string
    fallbackMessage: string
    escalationKeywords: string[]
    businessHoursOnly: boolean
    multilingual: boolean
    languages: string[]
  }
}

export interface GroqConfig {
  apiKey: string
  model: string
  enabled: boolean
  companySettings?: {
    name: string
    address: string
    phone: string
    email: string
    workingHours: string
    specialties: string
    warrantyPeriod: string
    averageRepairTime: string
    emergencyContact?: string
  }
  aiSettings?: {
    treatmentStyle: 'formal' | 'informal' | 'friendly' | 'professional'
    autoRecognizeCustomers: boolean
    accessToOrderHistory: boolean
    accessToCustomerData: boolean
    accessToInventory: boolean
    canProvideQuotes: boolean
    canScheduleAppointments: boolean
    maxResponseLength: number
    greetingMessage: string
    fallbackMessage: string
    escalationKeywords: string[]
    businessHoursOnly: boolean
    multilingual: boolean
    languages: string[]
  }
}

const WhatsAppIntegration: React.FC = () => {
  const { success, error } = useToast()

  // Estados
  const [status, setStatus] = useState<WhatsAppStatus | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [aiProvider, setAiProvider] = useState<'gemini' | 'groq'>('groq') // Padrão GROQ por ser gratuito
  const [geminiConfig, setGeminiConfig] = useState<GeminiConfig>({
    apiKey: '',
    model: 'gemini-1.5-flash',
    enabled: true,
    companySettings: {
      name: 'NexaMakers OS',
      address: '',
      phone: '',
      email: '',
      workingHours: 'Segunda a Sexta: 9h às 18h | Sábado: 9h às 13h',
      specialties: 'Reparação de smartphones, tablets, computadores, consolas de jogos',
      warrantyPeriod: '3 meses',
      averageRepairTime: '3-5 dias úteis',
      emergencyContact: ''
    },
    aiSettings: {
      treatmentStyle: 'friendly',
      autoRecognizeCustomers: true,
      accessToOrderHistory: true,
      accessToCustomerData: true,
      accessToInventory: true,
      canProvideQuotes: false,
      canScheduleAppointments: false,
      maxResponseLength: 500,
      greetingMessage: 'Olá! Como posso ajudar hoje?',
      fallbackMessage: 'Não consegui compreender. Pode reformular a pergunta?',
      escalationKeywords: ['reclamação', 'gerente', 'supervisor'],
      businessHoursOnly: false,
      multilingual: false,
      languages: ['pt']
    }
  })
  const [originalApiKey, setOriginalApiKey] = useState<string>('')
  const [groqConfig, setGroqConfig] = useState<GroqConfig>({
    apiKey: '',
    model: 'llama-3.3-70b-versatile',
    enabled: true,
    companySettings: {
      name: 'NexaMakers OS',
      address: '',
      phone: '',
      email: '',
      workingHours: 'Segunda a Sexta: 9h às 18h | Sábado: 9h às 13h',
      specialties: 'Reparação de smartphones, tablets, computadores, consolas de jogos',
      warrantyPeriod: '3 meses',
      averageRepairTime: '3-5 dias úteis',
      emergencyContact: ''
    },
    aiSettings: {
      treatmentStyle: 'friendly',
      autoRecognizeCustomers: true,
      accessToOrderHistory: true,
      accessToCustomerData: true,
      accessToInventory: true,
      canProvideQuotes: false,
      canScheduleAppointments: false,
      maxResponseLength: 500,
      greetingMessage: 'Olá! Como posso ajudar hoje?',
      fallbackMessage: 'Não consegui compreender. Pode reformular a pergunta?',
      escalationKeywords: ['reclamação', 'gerente', 'supervisor'],
      businessHoursOnly: false,
      multilingual: false,
      languages: ['pt']
    }
  })
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [isStopping, setIsStopping] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)

  // Helper functions para atualizar configurações da empresa
  const updateGroqCompanySettings = (field: string, value: string) => {
    setGroqConfig({
      ...groqConfig,
      companySettings: {
        name: groqConfig.companySettings?.name || '',
        address: groqConfig.companySettings?.address || '',
        phone: groqConfig.companySettings?.phone || '',
        email: groqConfig.companySettings?.email || '',
        workingHours: groqConfig.companySettings?.workingHours || '',
        specialties: groqConfig.companySettings?.specialties || '',
        warrantyPeriod: groqConfig.companySettings?.warrantyPeriod || '',
        averageRepairTime: groqConfig.companySettings?.averageRepairTime || '',
        emergencyContact: groqConfig.companySettings?.emergencyContact,
        [field]: value
      }
    })
  }

  // Carrega configurações iniciais
  useEffect(() => {
    loadStatus()
    loadGeminiConfig()
    loadGroqConfig()
  }, []) // Executa apenas uma vez ao montar

  // Polling separado para atualizar status
  useEffect(() => {
    // Determinar intervalo de polling baseado no estado
    let pollInterval = 5000 // Padrão: 5 segundos

    if (status?.ready) {
      pollInterval = 10000 // Se conectado: 10 segundos
    } else if (status?.hasQRCode || isStarting) {
      pollInterval = 2000 // Se mostrando QR Code ou iniciando: 2 segundos (mais rápido)
    }

    const interval = setInterval(() => {
      loadStatus()
    }, pollInterval)

    return () => clearInterval(interval)
  }, [status?.ready, status?.hasQRCode, isStarting])

  // Carrega status atual
  const loadStatus = async () => {
    try {
      const response = await apiService.get('/whatsapp/status')
      if (response.success) {
        setStatus(response.data)
        
        // Se tem QR Code, buscar imagem
        if (response.data.hasQRCode) {
          loadQRCodeImage()
        } else {
          setQrCodeImage(null)
        }
      }
    } catch (err) {
      console.error('Erro ao carregar status:', err)
    }
  }

  // Carrega configuração do Gemini
  const loadGeminiConfig = async () => {
    try {
      const response = await apiService.get('/whatsapp/gemini/config')
      if (response.success && response.data) {
        const apiKeyToUse = response.data.originalApiKey || response.data.apiKey || ''
        setOriginalApiKey(apiKeyToUse)
        
        setGeminiConfig({
          apiKey: response.data.apiKey || '',
          model: response.data.model || 'gemini-1.5-flash',
          enabled: response.data.enabled !== undefined ? response.data.enabled : true,
          companySettings: response.data.companySettings || geminiConfig.companySettings,
          aiSettings: response.data.aiSettings || geminiConfig.aiSettings
        })
      }
    } catch (err) {
      console.error('Erro ao carregar config Gemini:', err)
    }
  }

  // Carrega configuração do GROQ
  const loadGroqConfig = async () => {
    try {
      const response = await apiService.get('/whatsapp/groq/config')
      if (response.success && response.data) {
        setGroqConfig({
          apiKey: response.data.originalApiKey || '',
          model: response.data.model || 'llama-3.1-70b-versatile',
          enabled: response.data.enabled !== undefined ? response.data.enabled : true,
          companySettings: response.data.companySettings || groqConfig.companySettings,
          aiSettings: response.data.aiSettings || groqConfig.aiSettings
        })
      }
    } catch (err) {
      console.error('Erro ao carregar config GROQ:', err)
    }
  }

  // Carrega imagem do QR Code
  const loadQRCodeImage = async () => {
    try {
      const axiosInstance = apiService.getAxiosInstance()
      const response = await axiosInstance.get('/whatsapp/qrcode', {
        responseType: 'blob'
      })
      const imageUrl = URL.createObjectURL(response.data)
      setQrCodeImage(imageUrl)
    } catch (err) {
      console.error('Erro ao carregar QR Code:', err)
    }
  }

  // Salva configuração do GROQ
  const handleSaveGroq = async () => {
    if (!groqConfig.apiKey) {
      error('Erro', 'Chave API do GROQ é obrigatória')
      return
    }

    setIsLoading(true)
    try {
      const response = await apiService.post('/whatsapp/groq/config', groqConfig)
      
      if (response.success) {
        success('Configuração GROQ salva com sucesso!')
        await loadGroqConfig() // Recarrega a configuração
      } else {
        throw new Error('Erro ao salvar configuração GROQ')
      }
    } catch (err: any) {
      error('Erro ao salvar configuração GROQ', err.message)
    } finally {
      setIsLoading(false)
    }
  }

  // Força reset completo do WhatsApp Web
  const handleForceReset = async () => {
    setIsLoading(true)
    try {
      const response = await apiService.post('/whatsapp/force-reset', {})
      
      if (response.success) {
        success('Reset completo realizado! Todos os processos foram eliminados.')
        await loadStatus() // Recarrega status
      } else {
        throw new Error('Erro no reset completo')
      }
    } catch (err: any) {
      error('Erro ao realizar reset completo', err.message)
    } finally {
      setIsLoading(false)
    }
  }

  // Inicia WhatsApp Web
  const handleStart = async () => {
    if (aiProvider === 'gemini') {
      const apiKeyToUse = geminiConfig.apiKey.includes('...') && originalApiKey 
        ? originalApiKey 
        : geminiConfig.apiKey
        
      if (!apiKeyToUse) {
        error('Erro', 'Chave API do Gemini é obrigatória')
        return
      }
    }

    setIsStarting(true)
    try {
      // Configurar IA baseado no provider selecionado
      if (aiProvider === 'gemini') {
        const apiKeyToUse = geminiConfig.apiKey.includes('...') && originalApiKey 
          ? originalApiKey 
          : geminiConfig.apiKey
          
        const configToSave = {
          ...geminiConfig,
          apiKey: apiKeyToUse
        }
        const geminiResponse = await apiService.post('/whatsapp/gemini/config', configToSave)

        if (!geminiResponse.success) {
          throw new Error('Erro ao configurar Gemini AI')
        }
      } else {
        const groqResponse = await apiService.post('/whatsapp/groq/config', groqConfig)

        if (!groqResponse.success) {
          throw new Error('Erro ao configurar GROQ AI')
        }
      }

      // Configurar WhatsApp Web
      const whatsappConfig: any = {
        provider: 'WHATSAPP_WEB',
        enabled: true,
        whatsappWeb: {
          sessionName: 'nexamakers_session',
          enabled: true,
          headless: true
        },
        aiProvider: aiProvider
      }
      
      if (aiProvider === 'gemini') {
        const apiKeyToUse = geminiConfig.apiKey.includes('...') && originalApiKey 
          ? originalApiKey 
          : geminiConfig.apiKey
          
        whatsappConfig.gemini = {
          apiKey: apiKeyToUse,
          model: geminiConfig.model || 'gemini-1.5-flash',
          enabled: true
        }
        // Desabilitar GROQ quando Gemini está selecionado
        whatsappConfig.groq = {
          apiKey: groqConfig.apiKey,
          model: groqConfig.model,
          enabled: false
        }
      } else {
        whatsappConfig.groq = {
          apiKey: groqConfig.apiKey,
          model: groqConfig.model,
          enabled: true
        }
        // Desabilitar Gemini quando GROQ está selecionado
        whatsappConfig.gemini = {
          apiKey: geminiConfig.apiKey,
          model: geminiConfig.model || 'gemini-1.5-flash',
          enabled: false
        }
      }
      
      const whatsappResponse = await apiService.post('/whatsapp/configure', whatsappConfig)

      if (!whatsappResponse.success) {
        throw new Error('Erro ao configurar WhatsApp Web')
      }

      // Agora iniciar o WhatsApp Web
      console.log('🚀 Iniciando WhatsApp Web...')
      const startResponse = await apiService.post('/whatsapp/start', {})
      console.log('📱 Resposta do start:', startResponse)

      if (!startResponse.success) {
        throw new Error(startResponse.message || 'Erro ao iniciar WhatsApp Web')
      }

      success(`WhatsApp Web + ${aiProvider === 'gemini' ? 'Gemini' : 'GROQ'} iniciado com sucesso!`)
      loadStatus()
    } catch (err: any) {
      error('Erro ao iniciar WhatsApp Web', err.message)
    } finally {
      setIsStarting(false)
    }
  }

  // Para WhatsApp Web
  const handleStop = async () => {
    setIsStopping(true)
    try {
      console.log('🔄 Iniciando desconexão do WhatsApp Web...')
      const response = await apiService.post('/whatsapp/disconnect')

      console.log('📱 Resposta da desconexão:', response)

      if (!response.success) {
        throw new Error(response.message || 'Erro ao desconectar WhatsApp Web')
      }

      success(response.message || 'WhatsApp Web desconectado com sucesso!')

      // Resetar estados completamente
      setStatus({
        configured: false,
        ready: false,
        hasQRCode: false
      })
      setQrCodeImage(null)

      // Forçar atualização do status após desconexao
      setTimeout(() => {
        loadStatus()
      }, 1000)

    } catch (err: any) {
      console.error('❌ Erro na desconexão:', err)
      error('Erro ao parar WhatsApp Web', err.message)
    } finally {
      setIsStopping(false)
    }
  }

  // Testa configuração
  const handleTest = async () => {
    setIsLoading(true)
    try {
      const response = await apiService.post('/whatsapp/test')
      
      if (response.success) {
        success('Teste realizado com sucesso!', JSON.stringify(response.data, null, 2))
      } else {
        error('Teste falhou', response.error)
      }
    } catch (err: any) {
      error('Erro no teste', err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = () => {
    if (!status?.configured) return 'text-gray-500'
    if (status.ready) return 'text-green-600'
    if (status.hasQRCode) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getStatusText = () => {
    if (!status?.configured) return 'Não configurado'
    if (status.ready) return 'Conectado'
    if (status.hasQRCode) return 'Aguardando autenticação'
    if (status.configured) return 'Inicializando...'
    return 'Desconectado'
  }

  const getStatusIcon = () => {
    if (!status?.configured) return <AlertCircle className="h-5 w-5" />
    if (status.ready) return <CheckCircle className="h-5 w-5" />
    if (status.hasQRCode) return <QrCode className="h-5 w-5" />
    return <AlertCircle className="h-5 w-5" />
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageCircle className="h-6 w-6 text-green-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">WhatsApp Web + IA</h3>
              <p className="text-sm text-gray-600">
                {aiProvider === 'groq' ? '100% Gratuito com GROQ' : 'Integração com Gemini AI'}
              </p>
            </div>
          </div>
          
          <div className={`flex items-center gap-2 ${getStatusColor()}`}>
            {getStatusIcon()}
            <span className="font-medium">{getStatusText()}</span>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Seleção do Provider de IA */}
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Provider de Inteligência Artificial</h4>
          <div className="flex gap-4 mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="aiProvider"
                value="groq"
                checked={aiProvider === 'groq'}
                onChange={() => setAiProvider('groq')}
                className="text-green-600"
              />
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gradient-to-r from-green-500 to-blue-500 rounded"></div>
                <span className="font-medium">GROQ (Nuvem)</span>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">100% Grátis</span>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="aiProvider"
                value="gemini"
                checked={aiProvider === 'gemini'}
                onChange={() => setAiProvider('gemini')}
                className="text-blue-600"
              />
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded"></div>
                <span className="font-medium">Google Gemini</span>
                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Pago/Quota</span>
              </div>
            </label>
          </div>
        </div>

        {/* Configuração da IA Selecionada */}
        <div>
          <h4 className="font-medium text-gray-900 mb-3">
            Configuração {aiProvider === 'gemini' ? 'Gemini AI' : 'GROQ AI'}
          </h4>
          <div className="space-y-4">
            {aiProvider === 'gemini' ? (
              <>
                <div className="relative">
                  <Input
                    label="Chave API do Gemini"
                    type={showApiKey ? "text" : "password"}
                    value={geminiConfig.apiKey}
                    onChange={(e) => setGeminiConfig({ ...geminiConfig, apiKey: e.target.value })}
                    placeholder="AIzaSy..."
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-9 text-gray-400 hover:text-gray-600 transition-colors"
                    title={showApiKey ? "Ocultar chave" : "Mostrar chave"}
                  >
                    {showApiKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Modelo"
                    value={geminiConfig.model}
                    onChange={(e) => setGeminiConfig({ ...geminiConfig, model: e.target.value })}
                    placeholder="gemini-1.5-flash"
                  />
                  <div className="flex items-center mt-8">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={geminiConfig.enabled}
                        onChange={(e) => setGeminiConfig({ ...geminiConfig, enabled: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">Ativar respostas automáticas</span>
                    </label>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h5 className="font-medium text-green-900">GROQ - Configuração Completa</h5>
                      <p className="text-sm text-green-700 mt-1">
                        ✅ Modelo Llama 3.3 70B na nuvem GROQ<br/>
                        🚀 100% gratuito, acesso rápido à IA<br/>
                        ⚡ Processamento ultra-rápido na nuvem<br/>
                        🔍 Acesso completo ao sistema e base de dados
                      </p>
                    </div>
                  </div>
                </div>

                {/* Configuração Básica */}
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-900">🔑 Configuração da API</h5>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="relative">
                        <Input
                          label="Chave API do GROQ"
                          type={showApiKey ? 'text' : 'password'}
                          value={groqConfig.apiKey}
                          onChange={(e) => setGroqConfig({ ...groqConfig, apiKey: e.target.value })}
                          placeholder="Insira sua chave API do GROQ"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                        >
                          {showApiKey ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      <Input
                        label="Modelo IA"
                        value={groqConfig.model}
                        onChange={(e) => setGroqConfig({ ...groqConfig, model: e.target.value })}
                        placeholder="llama-3.3-70b-versatile"
                      />
                    </div>
                  </div>

                  {/* Configurações da Empresa */}
                  <div className="space-y-4 border-t pt-6">
                    <h5 className="font-medium text-gray-900">🏢 Dados da Empresa</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Nome da Empresa"
                        value={groqConfig.companySettings?.name || ''}
                        onChange={(e) => updateGroqCompanySettings('name', e.target.value)}
                        placeholder="NexaMakers OS"
                      />
                      <Input
                        label="Telefone"
                        value={groqConfig.companySettings?.phone || ''}
                        onChange={(e) => updateGroqCompanySettings('phone', e.target.value)}
                        placeholder="+351 XXX XXX XXX"
                      />
                      <Input
                        label="Email"
                        type="email"
                        value={groqConfig.companySettings?.email || ''}
                        onChange={(e) => updateGroqCompanySettings('email', e.target.value)}
                        placeholder="info@nexamakers.com"
                      />
                      <Input
                        label="Morada"
                        value={groqConfig.companySettings?.address || ''}
                        onChange={(e) => updateGroqCompanySettings('address', e.target.value)}
                        placeholder="Rua Example, 123"
                      />
                      <Input
                        label="Horário de Funcionamento"
                        value={groqConfig.companySettings?.workingHours || ''}
                        onChange={(e) => updateGroqCompanySettings('workingHours', e.target.value)}
                        placeholder="Seg-Sex: 9h-18h | Sáb: 9h-13h"
                      />
                      <Input
                        label="Especialidades"
                        value={groqConfig.companySettings?.specialties || ''}
                        onChange={(e) => updateGroqCompanySettings('specialties', e.target.value)}
                        placeholder="Reparação de smartphones, tablets..."
                      />
                      <Input
                        label="Período de Garantia"
                        value={groqConfig.companySettings?.warrantyPeriod || ''}
                        onChange={(e) => setGroqConfig({
                          ...groqConfig,
                          companySettings: {
                            ...groqConfig.companySettings,
                            warrantyPeriod: e.target.value
                          }
                        })}
                        placeholder="3 meses"
                      />
                      <Input
                        label="Tempo Médio Reparação"
                        value={groqConfig.companySettings?.averageRepairTime || ''}
                        onChange={(e) => setGroqConfig({
                          ...groqConfig,
                          companySettings: {
                            ...groqConfig.companySettings,
                            averageRepairTime: e.target.value
                          }
                        })}
                        placeholder="3-5 dias úteis"
                      />
                    </div>
                  </div>

                  {/* Configurações da IA */}
                  <div className="space-y-4 border-t pt-6">
                    <h5 className="font-medium text-gray-900">🤖 Configurações da Inteligência Artificial</h5>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Estilo de Tratamento</label>
                        <select
                          value={groqConfig.aiSettings?.treatmentStyle || 'friendly'}
                          onChange={(e) => setGroqConfig({
                            ...groqConfig,
                            aiSettings: {
                              ...groqConfig.aiSettings,
                              treatmentStyle: e.target.value as any
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="formal">Formal (você, senhor/senhora)</option>
                          <option value="informal">Informal (tu, tratamento direto)</option>
                          <option value="friendly">Amigável (recomendado)</option>
                          <option value="professional">Profissional</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tamanho Máximo Resposta</label>
                        <input
                          type="number"
                          min="100"
                          max="2000"
                          value={groqConfig.aiSettings?.maxResponseLength || 500}
                          onChange={(e) => setGroqConfig({
                            ...groqConfig,
                            aiSettings: {
                              ...groqConfig.aiSettings,
                              maxResponseLength: parseInt(e.target.value)
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Acesso ao Sistema */}
                    <div className="space-y-3">
                      <h6 className="font-medium text-gray-800">🔍 Acessos ao Sistema</h6>
                      <div className="grid grid-cols-2 gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={groqConfig.aiSettings?.autoRecognizeCustomers || false}
                            onChange={(e) => setGroqConfig({
                              ...groqConfig,
                              aiSettings: {
                                ...groqConfig.aiSettings,
                                autoRecognizeCustomers: e.target.checked
                              }
                            })}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">Reconhecimento Automático de Clientes</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={groqConfig.aiSettings?.accessToOrderHistory || false}
                            onChange={(e) => setGroqConfig({
                              ...groqConfig,
                              aiSettings: {
                                ...groqConfig.aiSettings,
                                accessToOrderHistory: e.target.checked
                              }
                            })}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">Acesso ao Histórico de Ordens</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={groqConfig.aiSettings?.accessToCustomerData || false}
                            onChange={(e) => setGroqConfig({
                              ...groqConfig,
                              aiSettings: {
                                ...groqConfig.aiSettings,
                                accessToCustomerData: e.target.checked
                              }
                            })}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">Acesso aos Dados de Clientes</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={groqConfig.aiSettings?.accessToInventory || false}
                            onChange={(e) => setGroqConfig({
                              ...groqConfig,
                              aiSettings: {
                                ...groqConfig.aiSettings,
                                accessToInventory: e.target.checked
                              }
                            })}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">Acesso ao Inventário</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={groqConfig.aiSettings?.canProvideQuotes || false}
                            onChange={(e) => setGroqConfig({
                              ...groqConfig,
                              aiSettings: {
                                ...groqConfig.aiSettings,
                                canProvideQuotes: e.target.checked
                              }
                            })}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">Pode Fornecer Orçamentos</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={groqConfig.aiSettings?.canScheduleAppointments || false}
                            onChange={(e) => setGroqConfig({
                              ...groqConfig,
                              aiSettings: {
                                ...groqConfig.aiSettings,
                                canScheduleAppointments: e.target.checked
                              }
                            })}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">Pode Agendar Consultas</span>
                        </label>
                      </div>
                    </div>

                    {/* Mensagens Personalizadas */}
                    <div className="space-y-3">
                      <h6 className="font-medium text-gray-800">💬 Mensagens Personalizadas</h6>
                      <div className="grid grid-cols-1 gap-4">
                        <Input
                          label="Mensagem de Saudação"
                          value={groqConfig.aiSettings?.greetingMessage || ''}
                          onChange={(e) => setGroqConfig({
                            ...groqConfig,
                            aiSettings: {
                              ...groqConfig.aiSettings,
                              greetingMessage: e.target.value
                            }
                          })}
                          placeholder="Olá! Como posso ajudar hoje?"
                        />
                        <Input
                          label="Mensagem de Erro/Fallback"
                          value={groqConfig.aiSettings?.fallbackMessage || ''}
                          onChange={(e) => setGroqConfig({
                            ...groqConfig,
                            aiSettings: {
                              ...groqConfig.aiSettings,
                              fallbackMessage: e.target.value
                            }
                          })}
                          placeholder="Não consegui compreender. Pode reformular?"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={groqConfig.enabled}
                        onChange={(e) => setGroqConfig({ ...groqConfig, enabled: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm font-medium">Ativar GROQ IA</span>
                    </label>
                    <Button
                      onClick={handleSaveGroq}
                      isLoading={isLoading}
                      disabled={!groqConfig.apiKey}
                      size="md"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      💾 Salvar Configuração Completa
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Controles */}
        <div className="border-t pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Controle do Serviço</h4>
              <p className="text-sm text-gray-600">
                Iniciar WhatsApp Web com {aiProvider === 'gemini' ? 'Gemini AI' : 'GROQ AI'}
              </p>
            </div>
            
            <div className="flex gap-3">
              {status?.ready ? (
                <Button
                  variant="outline"
                  onClick={handleStop}
                  leftIcon={<StopCircle className="h-4 w-4" />}
                  isLoading={isStopping}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  Parar
                </Button>
              ) : status?.configured && status?.hasQRCode ? (
                <>
                  <Button
                    variant="outline"
                    isLoading={true}
                    disabled={true}
                    className="text-blue-600 border-blue-300"
                  >
                    Aguardando QR Code...
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleForceReset}
                    isLoading={isLoading}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleStart}
                  leftIcon={<PlayCircle className="h-4 w-4" />}
                  isLoading={isStarting}
                  disabled={aiProvider === 'gemini' ? !geminiConfig.apiKey : !groqConfig.apiKey}
                >
                  Iniciar
                </Button>
              )}

              {status?.ready && (
                <Button
                  variant="outline"
                  onClick={handleTest}
                  isLoading={isLoading}
                >
                  Testar
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* QR Code */}
        {status?.hasQRCode && (
          <div className="border-t pt-6">
            <div className="text-center">
              <h4 className="font-medium text-gray-900 mb-3">Escaneie o QR Code</h4>
              <p className="text-sm text-gray-600 mb-4">
                Abra o WhatsApp no seu telefone e escaneie o código para conectar
              </p>
              
              {qrCodeImage ? (
                <div className="inline-block p-4 bg-white border rounded-lg shadow-sm">
                  <img 
                    src={qrCodeImage} 
                    alt="QR Code WhatsApp" 
                    className="w-64 h-64 object-contain"
                  />
                </div>
              ) : (
                <div className="inline-flex items-center justify-center w-64 h-64 bg-gray-100 border rounded-lg">
                  <Loader className="h-8 w-8 text-gray-400 animate-spin" />
                  <span className="ml-2 text-gray-500">A carregar QR Code...</span>
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-4">
                O QR Code expira automaticamente. Se não conseguir escanear, pare e inicie novamente.
              </p>
            </div>
          </div>
        )}

        {/* Info da Conexão */}
        {status?.ready && status.clientInfo && (
          <div className="border-t pt-6">
            <h4 className="font-medium text-gray-900 mb-3">Informações da Conexão</h4>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Nome:</span>
                  <span className="ml-2 font-medium">{status.clientInfo.name || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Telefone:</span>
                  <span className="ml-2 font-medium">{status.clientInfo.phone || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-600">IA:</span>
                  <span className="ml-2 font-medium text-green-600">
                    {aiProvider === 'gemini' ? 'Gemini AI' : 'GROQ (Nuvem)'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Status:</span>
                  <span className="ml-2 font-medium text-green-600">
                    {status.clientInfo.connected ? 'Conectado' : 'Desconectado'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default WhatsAppIntegration